import api from './api';

// Configurar URL da API - Detecta automaticamente o host correto
const getApiUrl = (): string => {
  if ((import.meta as any).env?.VITE_API_URL) {
    return (import.meta as any).env.VITE_API_URL;
  }
  const currentHost = window.location.hostname;
  return `http://${currentHost}:3001`;
};

const API_URL = getApiUrl();

// ============================================================================
// INTERFACES E TIPOS
// ============================================================================

/**
 * Interface de ActiveOS (OS ativa em tempo real)
 */
export interface ActiveOS {
  id: string;
  tenantId: string;
  orderOfServiceId: string;
  userId: string;
  sessionId: string;
  startTime: string;
  lastHeartbeat: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  currentDuration: number; // Duração atual em horas
  lastHeartbeatMinutes: number; // Minutos desde último heartbeat
  orderOfService: {
    id: string;
    title: string;
    type: string;
    priority: string;
    tags: string[];
    estimatedHours: number | null;
    actualHours: number;
    startDate: string | null;
    demand: {
      id: string;
      title: string;
      project: {
        name: string;
        client: {
          name: string;
        };
      } | null;
    };
  };
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
}

/**
 * Response de listar OS ativas
 */
export interface ListActiveOSResponse {
  success: boolean;
  data: ActiveOS[];
}

/**
 * Response de heartbeat
 */
export interface HeartbeatResponse {
  success: boolean;
  message: string;
  data: {
    osId: string;
    timestamp: string;
  };
}

/**
 * Response de verificar se OS está ativa
 */
export interface CheckOSActiveResponse {
  success: boolean;
  data: {
    osId: string;
    isActive: boolean;
    timestamp: string;
  };
}

/**
 * Response de limpeza de OS inativas
 */
export interface CleanupInactiveOSResponse {
  success: boolean;
  message: string;
  data: {
    cleanedCount: number;
    timestamp: string;
  };
}

// ============================================================================
// FUNÇÕES DE API
// ============================================================================

/**
 * Listar todas as OS ativas da empresa (tenant)
 * GET /api/os/active
 */
export const listActiveOS = async (): Promise<ActiveOS[]> => {
  try {
    const response = await api.get<ListActiveOSResponse>('/os/active');
    return response.data.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Erro ao listar OS ativas'
    );
  }
};

/**
 * Atualizar heartbeat de uma OS ativa
 * POST /api/active-os/heartbeat
 */
export const updateHeartbeat = async (osId: string): Promise<HeartbeatResponse> => {
  try {
    const response = await api.post<HeartbeatResponse>('/active-os/heartbeat', {
      osId
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Erro ao atualizar heartbeat'
    );
  }
};

/**
 * Verificar se uma OS está ativa
 * GET /api/active-os/check/:osId
 */
export const checkOSActive = async (osId: string): Promise<CheckOSActiveResponse> => {
  try {
    const response = await api.get<CheckOSActiveResponse>(`/active-os/check/${osId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Erro ao verificar se OS está ativa'
    );
  }
};

/**
 * Limpar OS inativas antigas (heartbeat > 5 minutos)
 * POST /api/active-os/cleanup
 */
export const cleanupInactiveOS = async (): Promise<CleanupInactiveOSResponse> => {
  try {
    const response = await api.post<CleanupInactiveOSResponse>('/active-os/cleanup');
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Erro ao limpar OS inativas'
    );
  }
};

// ============================================================================
// HELPERS E UTILITÁRIOS
// ============================================================================

/**
 * Formatar duração em horas para HH:MM:SS
 */
export const formatActiveOSDuration = (hours: number): string => {
  const totalSeconds = Math.floor(hours * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;

  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

/**
 * Verificar se uma OS está realmente ativa (heartbeat recente)
 */
export const isOSReallyActive = (activeOS: ActiveOS): boolean => {
  return activeOS.isActive && activeOS.lastHeartbeatMinutes <= 5;
};

/**
 * Obter status de atividade baseado no heartbeat
 */
export const getOSActivityStatus = (activeOS: ActiveOS): 'active' | 'stale' | 'inactive' => {
  if (!activeOS.isActive) return 'inactive';
  if (activeOS.lastHeartbeatMinutes <= 5) return 'active';
  return 'stale';
};

/**
 * Obter cor do status de atividade
 */
export const getOSActivityStatusColor = (activeOS: ActiveOS): string => {
  const status = getOSActivityStatus(activeOS);
  switch (status) {
    case 'active':
      return 'green';
    case 'stale':
      return 'yellow';
    case 'inactive':
      return 'gray';
    default:
      return 'gray';
  }
};

/**
 * Obter label do status de atividade
 */
export const getOSActivityStatusLabel = (activeOS: ActiveOS): string => {
  const status = getOSActivityStatus(activeOS);
  switch (status) {
    case 'active':
      return 'Em Andamento';
    case 'stale':
      return 'Sem Movimento';
    case 'inactive':
      return 'Inativa';
    default:
      return 'Desconhecido';
  }
};

// ============================================================================
// REACT QUERY HOOKS (Para uso nos componentes)
// ============================================================================

/**
 * Keys para React Query
 */
export const activeOSKeys = {
  all: ['activeOS'] as const,
  lists: () => [...activeOSKeys.all, 'list'] as const,
  list: () => [...activeOSKeys.lists()] as const,
  check: (osId: string) => [...activeOSKeys.all, 'check', osId] as const,
};

export default {
  listActiveOS,
  updateHeartbeat,
  checkOSActive,
  cleanupInactiveOS,
  formatActiveOSDuration,
  isOSReallyActive,
  getOSActivityStatus,
  getOSActivityStatusColor,
  getOSActivityStatusLabel,
  activeOSKeys,
};
